import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import StaffSidebar from './StaffSidebar';
import ViewAsUserBanner from '../Staff/ViewAsUserBanner';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from '../Dashboard/NotificationBell';
import StaffApprovalPending from '../Staff/StaffApprovalPending';
import FeedbackWidget from '../Feedback/FeedbackWidget';

import { getStaffDisplayName } from '../../utils/staffDisplay';

const StaffLayout = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const location = useLocation();

    // Intercept pending approval users
    if (user?.registrationStatus === 'pending_approval') {
        return <StaffApprovalPending />;
    }

    useEffect(() => {
        if (user && user.isStaff) {
            const details = user.staffDetails;

            // 0. Check if data is sanitized (from localStorage) - Skip check if waiting for full sync
            const isSanitized = details && !details.personalData && details.contractData;

            if (isSanitized) {
                // Data is sanitized (privacy mode), so we trust the auth state for now
                // Do NOT redirect based on missing fields.
            } else {
                // 1. Check for basic data completeness
                const isInvalid = !details ||
                    !details.personalData?.cpf ||
                    !details.businessData?.cnpj ||
                    !details.bankingData?.account ||
                    !details.contractData?.contractValue;

                if (isInvalid) {
                    navigate('/staff-register', {
                        state: {
                            isUpdate: true,
                            currentData: details || {},
                            discordData: {
                                discordId: user.discordId,
                                name: user.name,
                                email: user.email,
                                avatar: user.avatar
                            }
                        }
                    });
                    return;
                }
            }

            // 2. Check for contract signature (New Restriction)
            const isContractSigned = details?.contractSigned === true; // Ensure explicit true
            const isTerminated = details?.contractData?.status === 'terminated';
            const isServicePage = location.pathname === '/staff/service-provision';

            // RESTRICTION: Criador de Conteúdo can ONLY access service-provision
            if (user.accessLevel === 'criador_de_conteudo' && !isServicePage) {
                navigate('/staff/service-provision');
                return;
            }

            if ((!isContractSigned || isTerminated) && !isServicePage) {
                navigate('/staff/service-provision');
            }
        }
    }, [user, navigate, location.pathname]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-blue-900 text-white">
            <ViewAsUserBanner />

            {/* Header Mobile */}
            <div className="lg:hidden bg-black/40 backdrop-blur-xl border-b border-white/10 p-4 sticky top-0 z-[60]">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={toggleSidebar}
                            className="text-white hover:bg-white/10"
                        >
                            <Menu className="h-5 w-5" />
                        </Button>
                        <h1 className="text-lg font-semibold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                            Staff Panel
                        </h1>
                    </div>
                    <div className="flex items-center space-x-2">
                        <NotificationBell iconClassName="text-white h-5 w-5" />
                        <span className="text-sm text-gray-300 hidden sm:inline">{getStaffDisplayName(user)}</span>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleLogout}
                            className="text-white hover:bg-white/10"
                        >
                            <LogOut className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            <div className="flex">
                {/* Sidebar Desktop */}
                <div className="hidden lg:block w-72 h-screen fixed left-0 top-0 z-[100]">
                    <StaffSidebar />
                </div>

                {/* Sidebar Mobile */}
                <AnimatePresence>
                    {sidebarOpen && (
                        <>
                            {/* Overlay */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="lg:hidden fixed inset-0 bg-black/80 z-[110] backdrop-blur-sm"
                                onClick={closeSidebar}
                            />

                            {/* Sidebar Drawer */}
                            <motion.div
                                initial={{ x: -300 }}
                                animate={{ x: 0 }}
                                exit={{ x: -300 }}
                                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                                className="lg:hidden fixed left-0 top-0 w-72 h-full z-[120] shadow-2xl"
                            >
                                <div className="h-full relative">
                                    <StaffSidebar onClose={closeSidebar} />
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={closeSidebar}
                                        className="absolute top-4 right-4 text-white hover:bg-white/10 lg:hidden"
                                    >
                                        <X className="h-5 w-5" />
                                    </Button>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>

                {/* Conteúdo Principal */}
                <main className="flex-1 lg:ml-72 p-4 lg:p-8 overflow-y-auto min-h-screen w-full">
                    <div className="max-w-7xl mx-auto">
                        <Outlet />
                    </div>
                </main>
            </div>
            <FeedbackWidget />
        </div>
    );
};

export default StaffLayout;
